import { MapPin, Mail, Instagram, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHead } from "../components/Reveal";

const externalNav = (url, label) => (e) => {
  if (window.self !== window.top) {
    e.preventDefault();
    try {
      window.top.location.href = url;
      return;
    } catch {}
    try {
      const w = window.open(url, "_blank", "noopener");
      if (w) return;
    } catch {}
    toast.info(label, { duration: 8000 });
  }
};

export default function Contact({ content }) {
  const c = content.contact;
  const waNumber = (c.whatsapp || "").replace(/[^0-9]/g, "");
  const instagramUrl = `https://instagram.com/${c.instagram}`;
  const emailUrl = `mailto:${c.email}`;

  return (
    <section id="contact" className="scroll-mt-20 py-24 sm:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead overline="Contact Us" title="Let's talk about your vehicle" />
        <div className="mt-14 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <div className="space-y-1">
              <div className="flex items-start gap-5 border-t border-white/10 py-6" data-testid="contact-area">
                <MapPin size={20} className="text-electric-bright mt-1 shrink-0" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500">Service Area</p>
                  <p className="mt-2 text-white font-medium">{c.service_area}</p>
                  <p className="mt-1 text-sm text-gray-400">Based in {c.location} — get in touch to check coverage for your area.</p>
                </div>
              </div>
              <a href={emailUrl} onClick={externalNav(emailUrl, `Email us at ${c.email}`)} className="flex items-start gap-5 border-t border-white/10 py-6 group" data-testid="contact-email-link">
                <Mail size={20} className="text-electric-bright mt-1 shrink-0" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500">Email</p>
                  <p className="mt-2 text-white font-medium group-hover:text-electric-bright transition-colors">{c.email}</p>
                </div>
              </a>
              {c.phone && (
                <a href={`tel:${c.phone.replace(/\s/g, "")}`} onClick={externalNav(`tel:${c.phone.replace(/\s/g, "")}`, `Call us: ${c.phone}`)} className="flex items-start gap-5 border-t border-white/10 py-6 group" data-testid="contact-phone-link">
                  <Phone size={20} className="text-electric-bright mt-1 shrink-0" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500">Phone</p>
                    <p className="mt-2 text-white font-medium group-hover:text-electric-bright transition-colors">{c.phone}</p>
                  </div>
                </a>
              )}
              <a
                href={instagramUrl}
                onClick={externalNav(instagramUrl, `Find us on Instagram: @${c.instagram} — instagram.com/${c.instagram}`)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-5 border-t border-b border-white/10 py-6 group"
                data-testid="contact-instagram-link"
              >
                <Instagram size={20} className="text-electric-bright mt-1 shrink-0" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500">Instagram</p>
                  <p className="mt-2 text-white font-medium group-hover:text-electric-bright transition-colors">@{c.instagram}</p>
                  <p className="mt-1 text-sm text-gray-400">Follow the latest builds, remaps and diagnostics work.</p>
                </div>
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="h-full border border-white/10 bg-coal p-10 sm:p-14 flex flex-col justify-between gap-10 relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(70%_60%_at_80%_10%,rgba(6,69,243,0.14),transparent_70%)] pointer-events-none" />
              <div className="relative">
                <p className="font-display text-3xl sm:text-4xl font-semibold uppercase tracking-tight text-white leading-tight">
                  The fastest way to reach us
                </p>
                <p className="mt-4 text-sm sm:text-base text-gray-400 leading-relaxed max-w-md">
                  Send a message with your vehicle details and what you're after — we'll get back to you with honest advice and a clear price.
                </p>
              </div>
              <div className="relative flex flex-col gap-4">
                {waNumber ? (
                  <a
                    href={`https://wa.me/${waNumber}?text=${encodeURIComponent("Hi TheMapLab — I'd like to enquire about a service for my vehicle.")}`}
                    onClick={externalNav(`https://wa.me/${waNumber}`, `WhatsApp us: ${c.whatsapp}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-button"
                    className="inline-flex items-center justify-center gap-3 bg-electric hover:bg-electric-bright text-white text-sm font-bold uppercase tracking-[0.18em] px-8 py-4 transition-colors shadow-[0_0_24px_rgba(6,69,243,0.35)]"
                  >
                    <MessageCircle size={17} /> Chat on WhatsApp
                  </a>
                ) : (
                  <a
                    href={`mailto:${c.email}?subject=${encodeURIComponent("Service enquiry — TheMapLab")}`}
                    onClick={externalNav(emailUrl, `Email us at ${c.email}`)}
                    data-testid="contact-email-button"
                    className="inline-flex items-center justify-center gap-3 bg-electric hover:bg-electric-bright text-white text-sm font-bold uppercase tracking-[0.18em] px-8 py-4 transition-colors shadow-[0_0_24px_rgba(6,69,243,0.35)]"
                  >
                    <Mail size={17} /> Email Us
                  </a>
                )}
                <a
                  href={instagramUrl}
                  onClick={externalNav(instagramUrl, `Find us on Instagram: @${c.instagram} — instagram.com/${c.instagram}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-button"
                  className="inline-flex items-center justify-center gap-3 border border-white/25 hover:border-white text-white text-sm font-bold uppercase tracking-[0.18em] px-8 py-4 transition-colors"
                >
                  <Instagram size={17} /> DM on Instagram
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
